// Shared look for simple "name" lists (branches, departments, ...): search strip with a count
// badge, then small coloured cards (initials avatar, name on the left, #id on the right).
// Shows 5 rows of cards, then scrolls. Presentation only — the page owns search + filtering.
import { Avatar, Box, Stack, TextField, InputAdornment, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TagRoundedIcon from "@mui/icons-material/TagRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import { QR } from "../../qrCode/components/qrUi";

const CARD_COLORS = ["#198754", "#2563eb", "#7c3aed", "#0891b2", "#d97706", "#db2777", "#4f46e5"];
export const nameColor = (name = "") => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return CARD_COLORS[h % CARD_COLORS.length];
};
export const nameInitials = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("") || "?";

const CARD_H = 56; // px
const GAP = 10; // px

export default function NameCardList({
    items = [],            // already filtered list
    getName,               // (item) => display name
    getId,                 // (item) => id (optional)
    search,
    onSearchChange,        // (value) => void  (page keeps its own rules, e.g. no leading space)
    searchPlaceholder = "Search",
    countIcon,             // icon element for the count badge
    countLabel = "item",   // singular label, e.g. "branch"
    countLabelPlural,      // plural label, e.g. "branches"
    watermark,             // faint icon element in each card
    fullText = false,      // show the whole name (wraps to more lines) instead of cutting it with "…"
    renderActions,         // optional (item, color) => small action buttons shown at the right of the card
}) {
    const cardH = fullText ? 64 : CARD_H; // used for the 5-row scroll height
    const plural = countLabelPlural || `${countLabel}s`;
    return (
        <Box sx={{ mt: 1, border: "1px solid #e5e7eb", borderRadius: 3, bgcolor: "#fff", overflow: "hidden" }}>
            {/* Search strip */}
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, px: { xs: 1.5, sm: 2 }, pt: 1.25, pb: 1.5, bgcolor: "#fafbfc", borderBottom: "1px solid #e5e7eb" }}>
                <Box sx={{ width: { xs: "100%", sm: 300 } }}>
                    <TextField
                        size="small"
                        placeholder={searchPlaceholder}
                        fullWidth
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                height: 40,
                                borderRadius: 2.5,
                                bgcolor: "#fff",
                                fontSize: 14,
                                "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e5e7eb" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                                "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(37,99,235,0.12)" },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.blue },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 19, color: search ? QR.blue : "#9ca3af" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                {/* count badge */}
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pl: 0.6, pr: 1.25, py: 0.5, borderRadius: 5, bgcolor: `${QR.main}0f`, border: `1px solid ${QR.main}33` }}>
                    <Box sx={{ minWidth: 24, height: 24, px: 0.6, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 800, color: "#fff", background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`, boxShadow: `0 4px 10px -6px ${QR.main}` }}>
                        {items.length}
                    </Box>
                    {countIcon && <Box sx={{ display: "flex", color: QR.main, "& svg": { fontSize: 16 } }}>{countIcon}</Box>}
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: QR.dark }}>
                        {search ? "matching " : ""}{items.length === 1 ? countLabel : plural}
                    </Typography>
                </Stack>
            </Box>

            {/* Cards: 5 rows, then scroll */}
            <Box
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    maxHeight: { xs: 5 * cardH + 4 * GAP + 24, sm: 5 * cardH + 4 * GAP + 32 },
                    overflowY: "auto",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: "4px" },
                    scrollbarWidth: "thin",
                    scrollbarColor: "#cbd5e1 transparent",
                }}
            >
                {items.length !== 0 ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: fullText
                        ? { xs: "minmax(0, 1fr)", sm: "repeat(auto-fill, minmax(250px, 1fr))" }
                        // phones: 2 columns only when each card still has room for the name next to avatar + #id
                        : { xs: "repeat(auto-fill, minmax(170px, 1fr))", sm: "repeat(auto-fill, minmax(190px, 1fr))" }, gap: `${GAP}px` }}>
                        {items.map((item, index) => {
                            const name = getName(item) || "";
                            const id = getId ? getId(item) : undefined;
                            const c = nameColor(name);
                            return (
                                <Box
                                    key={index}
                                    title={name}
                                    sx={{
                                        position: "relative",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        minWidth: 0,
                                        boxSizing: "border-box",
                                        ...(fullText ? { minHeight: cardH, py: 1 } : { height: CARD_H }),
                                        pl: 1.25,
                                        pr: 1,
                                        borderRadius: 2.5,
                                        background: [
                                            `radial-gradient(circle at 100% 0%, ${c}26 0, ${c}00 46px)`, // corner glow
                                            `linear-gradient(135deg, #ffffff 0%, #ffffff 45%, ${c}12 100%)`,
                                        ].join(", "),
                                        // faint dot pattern on the right side
                                        "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            bottom: 0,
                                            right: 0,
                                            width: "30%",
                                            backgroundImage: `radial-gradient(${c}55 1.1px, transparent 1.5px)`,
                                            backgroundSize: "12px 12px",
                                            WebkitMaskImage: "linear-gradient(90deg, transparent, #000 75%)",
                                            maskImage: "linear-gradient(90deg, transparent, #000 75%)",
                                            pointerEvents: "none",
                                        },
                                        // colour line that slides in on hover
                                        "&::after": {
                                            content: '""',
                                            position: "absolute",
                                            left: 0,
                                            bottom: 0,
                                            height: 2,
                                            width: "0%",
                                            background: `linear-gradient(90deg, ${c}, ${c}55)`,
                                            transition: "width .25s ease",
                                        },
                                        border: `1px solid ${c}2e`,
                                        borderLeft: `3px solid ${c}`,
                                        boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
                                        transition: "all .18s ease",
                                        "& .card-mark": { transition: "all .25s ease" },
                                        "&:hover": {
                                            "&::after": { width: "100%" },
                                            transform: "translateY(-2px)",
                                            borderColor: `${c}66`,
                                            borderLeftColor: c,
                                            boxShadow: `0 10px 20px -12px ${c}`,
                                            "& .card-mark": { opacity: 0.16, transform: "rotate(-8deg) scale(1.08)" },
                                        },
                                    }}
                                >
                                    {watermark && (
                                        <Box className="card-mark" sx={{ position: "absolute", right: -6, bottom: -8, display: "flex", color: c, opacity: 0.08, pointerEvents: "none", "& svg": { fontSize: 52 } }}>
                                            {watermark}
                                        </Box>
                                    )}
                                    <Avatar sx={{ position: "relative", width: 34, height: 34, flexShrink: 0, fontSize: 12.5, fontWeight: 800, color: "#fff", background: `linear-gradient(135deg, ${c}, ${c}b3)`, boxShadow: `0 0 0 3px #fff, 0 0 0 4px ${c}40` }}>
                                        {nameInitials(name)}
                                    </Avatar>
                                    {/* name (left) */}
                                    <Typography noWrap={!fullText} sx={{ position: "relative", flex: 1, minWidth: 0, fontSize: fullText ? 12.5 : 13.5, fontWeight: fullText ? 700 : 800, color: "#0f172a", ...(fullText && { whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }) }}>
                                        {name}
                                    </Typography>
                                    {/* id (right) */}
                                    {id !== undefined && id !== null && (
                                        <Box component="span" sx={{ position: "relative", flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 0.2, px: 0.8, py: 0.2, borderRadius: 5, fontSize: 11.5, fontWeight: 800, color: c, bgcolor: "#fff", border: `1px solid ${c}40`, boxShadow: "0 1px 2px rgba(16,24,40,0.06)" }}>
                                            <TagRoundedIcon sx={{ fontSize: 12 }} />
                                            {id}
                                        </Box>
                                    )}
                                    {renderActions && (
                                        <Box sx={{ position: "relative", flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                                            {renderActions(item, c)}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Stack alignItems="center" spacing={0.75} sx={{ py: 5 }}>
                        <TravelExploreRoundedIcon sx={{ fontSize: 30, color: "#cbd5e1" }} />
                        <Typography variant="body2" fontWeight={600} color="#64748b">
                            No Data Found
                        </Typography>
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
